/**
 * FASE 10 → FASE 13 — WizardDownloadPanel: UI post-submit unificata.
 *
 * FASE 13 UX: il cliente vede SOLO il PDF Colucci Unified brandizzato.
 * I template compagnia (AIG/Reale/Lloyd's) restano generati e archiviati
 * lato admin, ma non sono più esposti nel download panel pubblico.
 *
 * Uso in ogni wizard (D&O, Cyber, Fine Arts):
 *   <WizardDownloadPanel
 *      title="Preventivi D&O generati"
 *      downloads={submitResult.downloads}   // ora contiene solo colucci_unified
 *      submissionId={submitResult.id}
 *      ramoLabel="D&O"
 *   />
 *
 * downloads = [{ slug, name, url }] — url già include ?t=download_token.
 *             In FASE 13 il server ritorna un array con SOLO l'entry
 *             { slug: "colucci_unified", ... }.
 */
import React from "react";
import { Download, CheckCircle2, Mail, Star, ShieldCheck } from "lucide-react";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";
const FEATURED_SLUG = "colucci_unified";

function fullUrl(u) {
  if (!u) return "#";
  if (/^https?:/i.test(u)) return u;
  return `${API_ORIGIN}${u}`;
}

export function WizardDownloadPanel({
  title = "Preventivo generato",
  downloads = [],
  submissionId,
  ramoLabel = "assicurazione",
  clientName,
}) {
  const featured = downloads.find((d) => d.slug === FEATURED_SLUG);
  return (
    <div
      className="bg-white border border-emerald-200 rounded-2xl p-6 sm:p-8 shadow-sm"
      data-testid="wizard-download-panel"
    >
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-emerald-700" />
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl text-[#0F1F14] mb-2">
          {title}
        </h2>
        <p className="text-sm text-[#4A5E51] max-w-xl mx-auto">
          Questo è il tuo Questionario ufficiale <strong>Colucci Assicuratori</strong>:
          contiene tutte le informazioni raccolte ed è l&apos;unico documento
          che ti serve conservare.
        </p>
        {submissionId && (
          <p
            className="mt-3 inline-block text-xs bg-[#D9C49A]/25 text-[#163321] rounded-lg px-3 py-1"
            data-testid="wizard-download-submission-id"
          >
            Pratica #{String(submissionId).slice(0, 8)}
          </p>
        )}
      </div>

      {!featured ? (
        <div
          className="text-center py-4 text-sm text-[#7A1D1D]"
          data-testid="wizard-download-empty"
        >
          Nessun PDF disponibile per il download. La nostra agenzia riceverà
          comunque la pratica per elaborazione manuale.
        </div>
      ) : (
        <a
          key={featured.slug}
          href={fullUrl(featured.url)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`wizard-download-${featured.slug}`}
          className="group block relative p-6 rounded-xl border-2 border-[#C9A448] bg-gradient-to-br from-[#F0E7C8]/40 to-white hover:shadow-lg transition-all text-left mb-5"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 shrink-0 rounded-full bg-[#163321] flex items-center justify-center">
              <Star className="w-7 h-7 text-[#C9A448]" fill="#C9A448" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wide bg-[#163321] text-[#C9A448] px-2 py-0.5 rounded font-bold">
                  Il tuo documento
                </span>
                <span className="text-[10px] uppercase tracking-wide bg-[#C9A448]/20 text-[#163321] px-2 py-0.5 rounded font-semibold">
                  Versione definitiva
                </span>
              </div>
              <p className="font-heading text-lg text-[#0F1F14] leading-tight">
                Il tuo Questionario Colucci Assicuratori
              </p>
              <p className="text-xs text-[#4A5E51] mt-1">
                Include tutte le informazioni raccolte, brandizzato Colucci,
                pronto per firma digitale. Le versioni per le compagnie
                assicurative sono utilizzate internamente dai nostri consulenti.
              </p>
            </div>
            <Download className="w-5 h-5 text-[#163321] shrink-0 group-hover:scale-110 transition-transform" />
          </div>
        </a>
      )}

      <div
        className="rounded-lg border border-[#E5E2DB] bg-[#F8F4ED]/40 p-3 mb-4 flex items-start gap-2"
        data-testid="wizard-download-privacy-note"
      >
        <ShieldCheck className="w-4 h-4 text-[#4A5E51] mt-0.5 shrink-0" />
        <p className="text-[11px] text-[#4A5E51] leading-relaxed">
          I documenti in formato template compagnia (AIG · Reale · Lloyd&apos;s)
          restano archiviati in modo sicuro nel gestionale della nostra
          agenzia e vengono utilizzati esclusivamente per la valutazione
          assuntiva presso i mandanti.
        </p>
      </div>

      <div className="border-t border-[#E5E2DB] pt-4 mt-4 text-center">
        <p className="text-xs text-[#7A8E83] mb-1 flex items-center justify-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />
          Colucci Assicuratori ti ricontatterà entro 24-48h lavorative
          {clientName ? ` all'indirizzo indicato per ${clientName}` : ""}.
        </p>
        <p className="text-xs text-[#7A8E83]">
          Preventivo {ramoLabel} non vincolante — l&apos;accettazione definitiva è
          soggetta a valutazione della compagnia.
        </p>
      </div>
    </div>
  );
}
